"use client";

import { useCallback, useMemo, useState } from "react";
import { BacktestIndicatorOption } from "@/lib/interface/Backtest.interface";

const INDICATOR_ID_MAP: Record<string, string> = {
  PlasmaIndicator: "plasma",
  SupportResistanceIndicator: "supportresistance",
  BreakoutCarverIndicator: "breakout",
  FearCarverIndicator: "fearcarver",
  TcBreakoutIndicator: "tcbreakout",
  VectorCandleIndicator: "vectorcandles",
  TcTrendIndicator: "tctrend",
  DeltaTrendIndicator: "deltatrend",
  FearGreedIndicator: "feargreed",
  AuctionMarketTheoryIndicator: "auctionmarket",
  TurtleTradingIndicator: "turtle", // not registered in UI yet
};

export interface IndicatorCatalogState {
  activeIndicators: ActiveIndicator[];
  setActiveIndicators: (next?: ActiveIndicator[] | null) => void;
  activeIndicatorIds: string[];
  backtestableIndicatorIds: string[];
  backtestIndicators: BacktestIndicatorOption[];
}

export interface ActiveIndicator {
  id: string;
  timeframes: string[];
  backtestable?: boolean;
  label?: string;
}

export function useIndicatorCatalog(): IndicatorCatalogState {
  const [activeIndicators, setActiveIndicatorsState] = useState<
    ActiveIndicator[]
  >([]);

  const setActiveIndicators = useCallback((next?: ActiveIndicator[] | null) => {
    const normalized = Array.isArray(next) ? next : [];
    setActiveIndicatorsState(normalized);
  }, []);

  const activeIndicatorIds = useMemo(() => {
    if (!activeIndicators?.length) return [];
    return activeIndicators
      .map((ai) => INDICATOR_ID_MAP[ai.id])
      .filter((id): id is string => typeof id === "string");
  }, [activeIndicators]);

  const backtestableIndicatorIds = useMemo(() => {
    if (!activeIndicators?.length) return [];
    return activeIndicators
      .filter((ai) => ai.backtestable)
      .map((ai) => INDICATOR_ID_MAP[ai.id])
      .filter((id): id is string => typeof id === "string");
  }, [activeIndicators]);

  const backtestIndicators = useMemo(() => {
    if (!activeIndicators?.length) return [];
    return activeIndicators
      .filter((ai) => ai.backtestable)
      .map((ai) => ({
        id: ai.id,
        label: ai.label || ai.id,
      }));
  }, [activeIndicators]);

  return {
    activeIndicators,
    setActiveIndicators,
    activeIndicatorIds,
    backtestableIndicatorIds,
    backtestIndicators,
  };
}
